import { type IReactionDisposer, makeAutoObservable, runInAction } from 'mobx'
import { type IDropdownItem, Typography, Status, Box } from 'shared/ui'
import { logger } from 'shared/lib/logger'
import { getUrlEndPath } from 'shared/lib'
import { IntegrationKey, type Integration } from 'entities/Integrations'
import {
  ChatbotBookingType,
  type IAgentSecondStepPayload,
  type IBookingType,
  type IChatbotFullData,
} from 'entities/Chatbot'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ManageIntegrationConnectionStore } from 'features/ManageIntegrationConnection'
import { MeetingsStore } from 'widgets/Meetings/store/meetingsStore'
import styles from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/ui/styles.module.scss'
import type { AgentBookAppointmentStepsStore } from 'pages/chatbot/pages/agentBookAppointment/store/agentBookAppointmentStepsStore'
import { type IHandleError } from 'pages/chatbot/pages/agentBookAppointment/store/type'
import { type ICalendlyIntegrationMeetingCustom } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/store/types'

export type IEventLinkDropdownItem = IDropdownItem & {
  data: ICalendlyIntegrationMeetingCustom
}

export class ConnectCalendarStore {
  private _bookingType: IBookingType = ChatbotBookingType.TimeChat
  private _isCalendlyConnected = false
  private _isLoading = false
  private _integrationMap: Map<number | string, Integration> = new Map()
  private _eventLink: IEventLinkDropdownItem | null = null
  private _errorsMap: Map<string, string> = new Map()
  private _disposeFindEventLink: IReactionDisposer | null = null

  private _manageIntegrationConnectionStore = new ManageIntegrationConnectionStore()
  private _meetingsStore: MeetingsStore
  private _loading = false

  constructor(
    private _stepsStore: AgentBookAppointmentStepsStore,
    private _entities: IEntitiesStore
  ) {
    this._meetingsStore = new MeetingsStore({ contact: null }, this._entities)

    makeAutoObservable(this)
  }

  get disabled() {
    return this._loading
  }

  get stepsStoreLoading() {
    return this._stepsStore.loading
  }

  get agentActionsStore() {
    return this._stepsStore.agentActionsStore
  }

  get bookingType() {
    return this._bookingType
  }

  get isCalendlyConnected() {
    return this._isCalendlyConnected
  }

  get isLoading() {
    return this._isLoading
  }

  get errorsMap() {
    return this._errorsMap
  }

  get isMeetingLoading() {
    return this._meetingsStore.loading
  }

  get search() {
    return this._meetingsStore.search
  }

  get eventLink() {
    return this._eventLink
  }

  get bookingTypes() {
    return [
      {
        id: ChatbotBookingType.TimeChat,
        customLabel: (
          <div className={styles.customLabel}>
            <Box display={'flex'} alignItems={'baseline'} gap={1}>
              <Typography variant={'body-md-medium'} ariaLabel={'Chat'}>
                Chat to book a time
              </Typography>
            </Box>
            <Typography
              variant={'text-input-field-r'}
              ariaLabel={'Chat_description'}
              color={'var(--content-primary-disabled)'}
            >
              The AI Agent will text back and forth with the customer to find a time
            </Typography>
          </div>
        ),
      },
      {
        id: ChatbotBookingType.ShareLink,
        customLabel: (
          <div className={styles.customLabel}>
            <Typography variant={'body-md-medium'} ariaLabel={'link'}>
              Share booking link
            </Typography>
            <Typography
              variant={'text-input-field-r'}
              ariaLabel={'Link_description'}
              color={'var(--content-primary-tertiary)'}
            >
              The AI Agent will share the booking link for the customer to find a time
            </Typography>
          </div>
        ),
      },
    ]
  }

  get connectionList(): IDropdownItem[] {
    const list: IDropdownItem[] = []

    this._entities.integrationsStore
      .getIntegrations([IntegrationKey.calendly, IntegrationKey.hubspot])
      .forEach((item) => {
        this._integrationMap.set(item.id, item)

        if (item.key === IntegrationKey.calendly) {
          if (item.connected) {
            this.setIsCalendlyConnected(true)
            this._meetingsStore.onSelectedIntegration(item)
          }
          list.push({
            id: item.id,
            iconL: 'calendly',
            label: 'Calendly',
            labelRight: item.connected ? null : 'Connect',
            labelRightVariant: 'body-md-regular',
          })
        }

        if (item.key === IntegrationKey.hubspot) {
          list.push({
            id: item.id,
            iconL: 'hubspot',
            label: 'HubSpot meetings',
            labelContent: (
              <Box display={'flex'} alignItems={'center'} gap={1}>
                <Typography variant={'body-md-regular'} ariaLabel={'hubspot'}>
                  HubSpot meetings
                </Typography>
                <Status title={'Coming soon'} intent={'warning'} emphasis={'high'} size={'label'} />
              </Box>
            ),
            disabled: true,
          })
        }
      })
    return list
  }

  get meetingsList(): IDropdownItem[] {
    const meetingList: IDropdownItem[] = []

    this._meetingsStore.filteredMeetings?.forEach((meeting) => {
      const calendarEvent: ICalendlyIntegrationMeetingCustom = {
        name: meeting.name,
        scheduling_url: 'scheduling_url' in meeting ? meeting.scheduling_url : '',
        id: meeting.id,
      }
      const isConfigurationValid =
        'is_configuration_valid' in meeting ? meeting?.is_configuration_valid : false

      meetingList.push({
        id: meeting.id,
        label: calendarEvent.name,
        labelRight: getUrlEndPath(calendarEvent.scheduling_url),
        labelRightVariant: 'body-md-regular',
        data: calendarEvent,
        labelRightClassName: styles.labelRight,
        ...(!isConfigurationValid && {
          disabled: true,
          tooltipProps: {
            label: (
              <>
                This link is missing required fields in Calendly.
                <br />
                Update it in Calendly to use.
              </>
            ),
            placement: 'top',
          },
        }),
      })
    })

    return meetingList
  }

  get stepPayload(): IAgentSecondStepPayload {
    return {
      settings: {
        book_appointment_type: { value: this._bookingType },
        smart_booking_enabled: { value: this._smart_booking_enabled },
        book_appointment_link: {
          value: this._eventLink?.data.scheduling_url || '',
          id: this._eventLink?.data.id || '',
          name: this._eventLink?.data.name || '',
        },
      },
    }
  }

  get isChatBookingType() {
    return this._bookingType === ChatbotBookingType.TimeChat
  }

  get calendlyFieldsStore() {
    return this._stepsStore.calendlyFieldsStore
  }

  init = async (data: IChatbotFullData | null) => {
    if (!data) return

    const { book_appointment_type, book_appointment_link, smart_booking_enabled } =
      data.settings ?? {}

    this.setBookingType(book_appointment_type?.value ?? 'share_link')
    this.setSmartBookingEnabled(smart_booking_enabled?.value ?? true)
    this._eventLink = book_appointment_link
      ? {
          id: book_appointment_link.id,
          label: book_appointment_link.name,
          labelRight: getUrlEndPath(book_appointment_link?.value || ''),
          labelRightVariant: 'body-md-regular',
          data: {
            ...book_appointment_link,
            scheduling_url: book_appointment_link?.value,
          },
        }
      : null
  }

  setSearch = (search: string) => {
    this._meetingsStore.setSearch(search)
  }

  setConnection = async (connection: IDropdownItem) => {
    if (this._isCalendlyConnected) return
    const integration = this._integrationMap.get(connection.id)

    if (!integration) return

    try {
      runInAction(() => {
        this._isLoading = true
      })

      await this._manageIntegrationConnectionStore.connect(integration)
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isLoading = false
      })
    }
  }

  setLink = (link: IDropdownItem) => {
    this._eventLink = link as IEventLinkDropdownItem

    this.calendlyFieldsStore.fetchFields({
      params: String(this._eventLink.id),
    })
  }

  setError = (key: string, error: string) => {
    this._errorsMap.set(key, error)
  }

  removeError = (key: string) => {
    this._errorsMap.delete(key)
  }

  setBookingType = (bookingType: IBookingType) => {
    this._bookingType = bookingType
  }

  setIsCalendlyConnected = (isConnected: boolean) => {
    this._isCalendlyConnected = isConnected
  }

  setConnectionError = () => {
    this.setError('connection', 'Select a scheduling tool to connect your calendar.')
  }

  checkValidate = () => {
    let validate = true
    if (!this._isCalendlyConnected) {
      this.setConnectionError()
      validate = false
    }

    if (!this._eventLink) {
      this.setError('book_appointment_link', 'Select an event to share with contacts.')
      validate = false
    }

    return validate
  }

  handleError = (errors: IHandleError) =>
    Object.entries(errors).forEach(([errorKey, errorMessages]) => {
      const processedErrorKey = errorKey.includes('.') ? errorKey.split('.')[1] : errorKey

      if (!this._isCalendlyConnected && processedErrorKey === 'book_appointment_link') {
        return this.setConnectionError()
      }

      this.setError(
        processedErrorKey,
        Array.isArray(errorMessages) ? errorMessages[0] : errorMessages
      )
    })

  handleSaveDraft = async (id?: string, shouldRedirect = true) => {
    try {
      if (this._loading) return false

      runInAction(() => {
        this._loading = true
      })

      await this._stepsStore.quickSaveDraft('step_2', { shouldRedirect, id: Number(id) })
      return true
    } catch (e) {
      this._errorsMap.clear()
      this._stepsStore.handleApiError(e, (errors) => this.handleError(errors))
      return false
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  handleNextClick = async (id?: string) => {
    if (!this.checkValidate()) return

    if (this._loading) return false

    runInAction(() => {
      this._loading = true
    })

    try {
      await this._stepsStore.handleStepValidationAndNavigation('step_2', this.stepPayload, id)
    } catch (e) {
      this._stepsStore.handleValidationApiError(e, this.handleError)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  dispose = () => {
    this._disposeFindEventLink?.()
  }

  private _smart_booking_enabled = true
  setSmartBookingEnabled = (isEnabled: boolean) => {
    this._smart_booking_enabled = isEnabled
  }

  get smartBookingEnabled() {
    return this._smart_booking_enabled
  }
}
